<template>
  <Modal
    v-model="visible"
    title="导入录音"
    width="700px"
    :mask-closable="false"
    @on-ok="handleOk"
    @on-cancel="handleCancel"
  >
    <div class="audio-import-container">
      <div class="upload-section">
        <h3>上传录音压缩包</h3>
        <p class="desc">请上传包含录音文件的ZIP压缩包，录音文件命名必须与导出文件中的ID一致</p>
        
        <Upload
          ref="audioUpload"
          :before-upload="beforeAudioUpload"
          :format="['zip']"
          :max-size="102400"
          accept=".zip"
          :show-upload-list="false"
        >
          <Button type="primary" size="large">
            <Icon type="md-cloud-upload" />
            选择ZIP压缩包
          </Button>
        </Upload>
        
        <p class="upload-tip">支持 .zip 格式，文件大小不超过100MB</p>
        
        <div class="zip-tips">
          <h4>ZIP文件创建建议：</h4>
          <ul>
            <li>使用专业压缩软件（如7-Zip、WinRAR）创建ZIP文件</li>
            <li>避免使用macOS系统自带的压缩功能，会包含系统文件</li>
            <li>确保ZIP文件内只包含音频文件，不要包含文件夹</li>
            <li>录音文件命名格式：{{ sceneId }}-scenes-F00001-P00001-V00001.wav</li>
          </ul>
        </div>
      </div>

      <div v-if="uploadProgress.length > 0" class="progress-section">
        <h3>上传进度</h3>
        <div class="progress-list">
          <div 
            v-for="item in uploadProgress" 
            :key="item.id" 
            class="progress-item"
            :class="{ 
              'success': item.status === 'success',
              'error': item.status === 'error',
              'uploading': item.status === 'uploading'
            }"
          >
            <div class="file-info">
              <span class="file-name">{{ item.fileName }}</span>
              <span class="file-id">{{ item.id }}</span>
            </div>
            <div class="status-info">
              <span v-if="item.status === 'uploading'" class="status uploading">
                <Icon type="ios-loading" class="loading-icon" />
                上传中...
              </span>
              <span v-else-if="item.status === 'success'" class="status success">
                <Icon type="ios-checkmark-circle" />
                成功
              </span>
              <span v-else-if="item.status === 'error'" class="status error">
                <Icon type="ios-close-circle" />
                {{ item.error || '失败' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="importResult.length > 0" class="result-section">
        <h3>导入结果</h3>
        <div class="result-list">
          <div 
            v-for="item in importResult" 
            :key="item.id" 
            class="result-item"
            :class="{ 
              'success': item.status === 'success',
              'error': item.status === 'error'
            }"
          >
            <div class="file-info">
              <span class="file-name">{{ item.fileName }}</span>
              <span class="file-id">{{ item.id }}</span>
            </div>
            <div class="status-info">
              <span v-if="item.status === 'success'" class="status success">
                <Icon type="ios-checkmark-circle" />
                已更新语音文件
              </span>
              <span v-else-if="item.status === 'error'" class="status error">
                <Icon type="ios-close-circle" />
                {{ item.error || '更新失败' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div slot="footer">
      <Button @click="handleCancel">取消</Button>
      <Button 
        type="primary" 
        @click="handleOk" 
        :disabled="uploadProgress.length === 0 || hasUploading"
      >
        完成
      </Button>
    </div>
  </Modal>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import JSZip from 'jszip'
import { uploadA } from '@/server/api'
import { getToken } from '@/utils/auth'

export default {
  name: 'AudioImportModal',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      uploadProgress: [],
      importResult: [],
      zipFile: null
    }
  },
  computed: {
    ...mapState('scene', ['scene', 'sceneId']),
    ...mapState('flow', ['curFlowId']),
    visible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    hasUploading() {
      return this.uploadProgress.some(item => item.status === 'uploading')
    }
  },
  methods: {
    ...mapActions('scene', ['saveScene']),
    
    // 音频文件上传前处理
    beforeAudioUpload(file) {
      this.zipFile = file
      this.processZipFile(file)
      return false
    },

    // 处理ZIP文件
    async processZipFile(file) {
      try {
        this.$Message.loading({
          content: '正在解析ZIP文件...',
          duration: 0
        })

        const zip = new JSZip()
        const zipContent = await zip.loadAsync(file)
        
        // 过滤出音频文件，排除macOS系统文件和隐藏文件
        const audioFiles = []
        for (const [fileName, zipEntry] of Object.entries(zipContent.files)) {
          // 跳过目录、macOS系统文件、隐藏文件和DS_Store文件
          if (zipEntry.dir) continue
          if (fileName.startsWith('__MACOSX/')) continue
          if (fileName.startsWith('.')) continue
          if (fileName.includes('/.DS_Store')) continue
          if (fileName.includes('/Thumbs.db')) continue
          if (fileName.includes('/desktop.ini')) continue
          
          // 只处理音频文件
          if (this.isAudioFile(fileName)) {
            audioFiles.push({
              fileName,
              zipEntry,
              id: this.extractIdFromFileName(fileName)
            })
          }
        }

        if (audioFiles.length === 0) {
          this.$Message.destroy()
          this.$Message.error('ZIP文件中没有找到有效的音频文件')
          return
        }

        this.$Message.destroy()
        this.$Message.success(`找到 ${audioFiles.length} 个音频文件`)
        
        // 初始化上传进度
        this.uploadProgress = audioFiles.map(file => ({
          id: file.id,
          fileName: file.fileName,
          status: 'pending',
          error: null
        }))

        // 开始上传
        await this.uploadAudioFiles(audioFiles)
        
      } catch (error) {
        this.$Message.destroy()
        console.error('ZIP文件处理失败:', error)
        this.$Message.error('ZIP文件处理失败')
      }
    },

    // 判断是否为音频文件
    isAudioFile(fileName) {
      const audioExtensions = ['.wav', '.mp3', '.m4a', '.aac', '.ogg']
      const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
      return audioExtensions.includes(extension)
    },

    // 从文件名提取ID
    extractIdFromFileName(fileName) {
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'))
      return nameWithoutExt
    },

    // 上传音频文件
    async uploadAudioFiles(audioFiles) {
      const newScene = JSON.parse(JSON.stringify(this.scene))
      const uploadPromises = audioFiles.map(async (file, index) => {
        try {
          // 更新状态为上传中
          this.updateProgressStatus(file.id, 'uploading')
          
          // 将ZIP条目转换为Blob
          const audioBlob = await file.zipEntry.async('blob')
          
          // 创建FormData
          const formData = new FormData()
          formData.append('file', audioBlob, file.fileName)
          
          // 上传文件
          const response = await this.uploadFile(formData)
          
          if (response.code === 200) {
            this.updateProgressStatus(file.id, 'success')
            await this.updateSceneAudio(file.id, response.data.filepath, newScene)
          } else {
            throw new Error(response.message || '上传失败')
          }
          
        } catch (error) {
          console.error(`文件 ${file.fileName} 上传失败:`, error)
          this.updateProgressStatus(file.id, 'error', error.message)
        }
      })

      await Promise.all(uploadPromises)
      await this.saveScene(newScene)
      this.processImportResults()
    },

    // 上传单个文件
    async uploadFile(formData) {
      const response = await fetch(uploadA, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': this.getToken()
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      return await response.json()
    },

    // 获取token
    getToken() {
      return getToken()
    },

    // 更新进度状态
    updateProgressStatus(id, status, error = null) {
      const item = this.uploadProgress.find(item => item.id === id)
      if (item) {
        item.status = status
        item.error = error
      }
    },

    // 更新场景中的音频文件
    async updateSceneAudio(id, filePath, newScene) {
      try {
        const parts = id.split('-')
        if (parts.length < 3) return;
        const sceneId = parts[0]
        if (sceneId !== `${this.sceneId}`) return;
        const type = parts[1]
        if (type === 'scenes' && parts.length === 5) {
          const [, , sceneId, nodeId, voiceId] = parts
          const scene = newScene.scenes.find(scene => scene.id === sceneId)
          if (!scene) return;
          const node = scene.nodes.find(node => node.id === nodeId)
          if (!node) return;
          const voice = node.voices.find(voice => voice.id === voiceId)
          if (!voice) return;
          voice.voice = filePath
        } else if (type === 'knowledge' && parts.length === 4) {
          const [, , knowledgeId, responseId] = parts
          const knowledge = newScene.knowledge.find(knowledge => knowledge.id === knowledgeId)
          if (!knowledge) return;
          const response = knowledge.responses.find(response => response.id === responseId)
          if (!response) return;
          response.voice = filePath
        } else if (type === 'timeout' && parts.length === 3) {
          const [, , voiceId] = parts
          if (!newScene.settings.general.timeout_voices) return;
          const timeoutVoice = newScene.settings.general.timeout_voices.find(voice => voice.id === voiceId)
          if (!timeoutVoice) return;
          timeoutVoice.voice = filePath
        } else if (type === 'multiPrologue' && parts.length === 3) {
          const [, , voiceId] = parts
          if (!newScene.settings.multi_prologue.voices) return;
          const multiPrologueVoice = newScene.settings.multi_prologue.voices.find(voice => voice.id === voiceId)
          if (!multiPrologueVoice) return;
          multiPrologueVoice.voice = filePath
        }
        
      } catch (error) {
        console.error('更新场景音频失败:', error)
        throw error
      }
    },

    // 处理导入结果
    processImportResults() {
      this.importResult = this.uploadProgress.map(item => ({
        id: item.id,
        fileName: item.fileName,
        status: item.status,
        error: item.error
      }))
    },

    handleOk() {
      this.visible = false
      this.resetData()
    },

    handleCancel() {
      this.visible = false
      this.resetData()
    },

    resetData() {
      this.uploadProgress = []
      this.importResult = []
      this.zipFile = null
    }
  }
}
</script>

<style lang="scss" scoped>
.audio-import-container {
  .upload-section {
    margin-bottom: 24px;
    padding: 16px;
    border: 1px solid #e8eaec;
    border-radius: 6px;
    background-color: #fafafa;

    h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 600;
      color: #17233d;
    }

    .desc {
      margin: 0 0 16px 0;
      color: #808695;
      font-size: 14px;
      line-height: 1.5;
    }

    .upload-tip {
      margin: 8px 0 0 0;
      color: #c5c8ce;
      font-size: 12px;
    }

    .zip-tips {
      margin-top: 20px;
      padding-top: 16px;
      border-top: 1px solid #e8eaec;

      h4 {
        margin: 0 0 8px 0;
        font-size: 14px;
        font-weight: 600;
        color: #17233d;
      }

      ul {
        padding-left: 20px;
        list-style: disc;

        li {
          margin-bottom: 4px;
          color: #515a6e;
          font-size: 13px;
          line-height: 1.6;
        }
      }
    }
  }

  .progress-section,
  .result-section {
    margin-bottom: 24px;

    h3 {
      margin: 0 0 16px 0;
      font-size: 16px;
      font-weight: 600;
      color: #17233d;
    }

    .progress-list,
    .result-list {
      max-height: 300px;
      overflow-y: auto;
      border: 1px solid #e8eaec;
      border-radius: 6px;

      .progress-item,
      .result-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        &.success {
          background-color: #f6ffed;
        }

        &.error {
          background-color: #fff2f0;
        }

        &.uploading {
          background-color: #f0f9ff;
        }

        .file-info {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .file-name {
            font-weight: 500;
            color: #17233d;
          }

          .file-id {
            font-size: 12px;
            color: #808695;
            font-family: monospace;
          }
        }

        .status-info {
          .status {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 14px;

            &.success {
              color: #19be6b;
            }

            &.error {
              color: #ed4014;
            }

            &.uploading {
              color: #2d8cf0;

              .loading-icon {
                animation: spin 1s linear infinite;
              }
            }
          }
        }
      }
    }
  }
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
